import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  DocumentTextIcon,
  StarIcon,
  TrashIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { Card, Button, Loading, Modal } from '../components/common';
import { getResumes, deleteResume, setDefaultResume } from '../services/resumeService';

const ResumesPage = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, resume: null });
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const response = await getResumes();
      if (response.success) {
        setResumes(response.data.resumes);
      }
    } catch (err) {
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultResume(id);
      setResumes(resumes.map(r => ({
        ...r,
        isDefault: r.id === id
      })));
      toast.success('Default resume updated');
    } catch (err) {
      toast.error('Failed to update default resume');
    }
    setActionMenuOpen(null);
  };

  const handleDelete = async () => {
    if (!deleteModal.resume) return;

    try {
      await deleteResume(deleteModal.resume.id);
      setResumes(resumes.filter(r => r.id !== deleteModal.resume.id));
      toast.success('Resume deleted');
    } catch (err) {
      toast.error('Failed to delete resume');
    } finally {
      setDeleteModal({ open: false, resume: null });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" text="Loading resumes..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resumes</h1>
          <p className="mt-1 text-gray-600">
            Manage your master resumes for job applications
          </p>
        </div>
        <Link to="/resumes/new">
          <Button>
            <PlusIcon className="h-5 w-5 mr-2" />
            Upload Resume
          </Button>
        </Link>
      </div>

      {/* Empty state */}
      {resumes.length === 0 && (
        <Card className="text-center py-12">
          <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No resumes yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Upload your resume and our AI will parse it into a structured format
            that can be tailored for each job application.
          </p>
          <Link to="/resumes/new">
            <Button>
              <PlusIcon className="h-5 w-5 mr-2" />
              Upload Your First Resume
            </Button>
          </Link>
        </Card>
      )}

      {/* Resume list */}
      {resumes.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {resumes.map((resume) => (
            <Card key={resume.id} className="relative hover:shadow-md transition-shadow">
              {/* Default badge */}
              {resume.isDefault && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                    <StarSolidIcon className="h-3 w-3" />
                    Default
                  </span>
                </div>
              )}

              {/* Content */}
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/resumes/${resume.id}`)}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {resume.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {resume.contactInfo?.fullName || 'No name'}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center py-4 border-t border-gray-100">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {resume.experience?.length || 0}
                    </p>
                    <p className="text-xs text-gray-500">Jobs</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {resume.skills?.length || 0}
                    </p>
                    <p className="text-xs text-gray-500">Skills</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {resume.education?.length || 0}
                    </p>
                    <p className="text-xs text-gray-500">Edu</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  Updated {formatDate(resume.updatedAt)}
                </span>

                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionMenuOpen(actionMenuOpen === resume.id ? null : resume.id);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </button>

                  {actionMenuOpen === resume.id && (
                    <div className="absolute right-0 bottom-full mb-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/resumes/${resume.id}`);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      {!resume.isDefault && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefault(resume.id);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <StarIcon className="h-4 w-4" />
                          Set as Default
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({ open: true, resume });
                          setActionMenuOpen(null);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, resume: null })}
        title="Delete Resume"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete &quot;{deleteModal.resume?.name}&quot;?
          This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={() => setDeleteModal({ open: false, resume: null })}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ResumesPage;
